import download from "downloadjs";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import moment from "moment";
import { publicRequest } from "../../requestMethods";
import { getPaymentTypeLabel } from "../../constants/paymentTypeOptions";
import {
    PAYMENT_SLOTS,
    paymentAmountField,
    paymentReceiptField,
    paymentReceiptType,
} from "../../constants/paymentSlots";

const PAYMENT_SLOT_BY_RECEIPT_TYPE = Object.fromEntries(
    PAYMENT_SLOTS.map((slot) => [paymentReceiptType(slot), paymentAmountField(slot)])
);

const PAYMENT_SLOT_FIELDS = Object.values(PAYMENT_SLOT_BY_RECEIPT_TYPE);

const RECEIPT_NO_BY_SLOT = Object.fromEntries(
    PAYMENT_SLOTS.map((slot) => [paymentAmountField(slot), paymentReceiptField(slot)])
);

const PAYMENT_INDEX_BY_SLOT = Object.fromEntries(
    PAYMENT_SLOTS.map((slot) => [paymentAmountField(slot), slot.index])
);

const resolveDisplayReceiptNo = (order, paymentSlot, receiptNumber) => {
    const stored = sanitizeText(
        String(
            (paymentSlot && order?.[RECEIPT_NO_BY_SLOT[paymentSlot]]) ||
                receiptNumber ||
                ""
        ).trim()
    );
    if (stored) return stored;

    const coNumber = String(order?.coNumber || "").trim();
    const paymentIndex = PAYMENT_INDEX_BY_SLOT[paymentSlot];
    if (coNumber && paymentIndex) return `${coNumber}-${paymentIndex}`;
    return "-";
};

const DEFAULT_PAYMENT_MODE = "Cash / Cheque / Credit Card / Bank Transfer";

const toNumber = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
};

const getPaymentParticular = (order, paymentSlot) => {
    const remainingBalance =
        Math.round(
            (toNumber(order?.amount) -
                toNumber(order?.discount) -
                toNumber(order?.deposit)) *
                100
        ) / 100;
    const lastPaymentSlot = [...PAYMENT_SLOT_FIELDS]
        .reverse()
        .find((field) => toNumber(order?.[field]) > 0);

    return remainingBalance <= 0 && paymentSlot === lastPaymentSlot
        ? "Balance"
        : "Deposit";
};

const getPaymentMode = (order, paymentSlot) => {
    const slotPaymentType = paymentSlot ? order?.[`${paymentSlot}Type`] : null;
    return slotPaymentType
        ? getPaymentTypeLabel(slotPaymentType)
        : DEFAULT_PAYMENT_MODE;
};

const DEFAULT_RECEIPT_HEADER = {
    name: "INHOME GROUP SDN BHD",
    addressLine1: "No.3A, Jalan KPB 18, Kawasan Perindustrian",
    addressLine2: "Balakong, 43300 Seri Kembangan Selangor. D.E",
    detailLines: [],
};

const hasMeaningfulAddressPart = (v) => {
    if (v === undefined || v === null) return false;
    const s = String(v).trim();
    return s !== "" && s !== "-";
};

const sanitizeText = (text) => {
    if (!text) return "";
    return String(text)
        .replace(/[\u2018\u2019\u2032\u02BC\u00B4\uFF07]/g, "'")
        .replace(/[\u201C\u201D\u2033\u2036]/g, '"')
        .replace(/\uFF08/g, "(")
        .replace(/\uFF09/g, ")")
        .replace(/[^\x20-\x7E\xA0-\xFF]/g, "?");
};

const formatPrincipleAddressLines = (address = {}) => {
    const line1 = String(address.lineOne || "").trim();
    const line2 = String(address.lineTwo || "").trim();
    const zip = String(address.zip || "").trim();
    const city = String(address.city || "").trim();

    let addressLine1 = DEFAULT_RECEIPT_HEADER.addressLine1;
    if (hasMeaningfulAddressPart(line1) || hasMeaningfulAddressPart(line2)) {
        if (!hasMeaningfulAddressPart(line1)) {
            addressLine1 = line2;
        } else if (!hasMeaningfulAddressPart(line2)) {
            addressLine1 = line1.endsWith(",") ? line1 : `${line1},`;
        } else {
            addressLine1 = `${line1}, ${line2}`;
        }
    }

    const cityLineParts = [zip, city].filter((part) => hasMeaningfulAddressPart(part));
    const addressLine2 =
        cityLineParts.length > 0
            ? cityLineParts.join(" ")
            : DEFAULT_RECEIPT_HEADER.addressLine2;

    return { addressLine1, addressLine2 };
};

const PRINCIPLE_BUSINESS_DETAIL_ROWS = [
    { field: "businessRegNo", label: "Business Registration Number" },
    { field: "tin", label: "Business TIN" },
    { field: "salesTaxNo", label: "Business Sales Tax Number" },
    { field: "bankAccount", label: "Bank Account Number" },
];

/** Company names often embed the registration number, e.g. "ABC SDN BHD (202101002744 (1403042K))". */
const stripRegistrationFromName = (name) =>
    name.replace(/\s*[(\uFF08]?\s*\d{6,}[\s\S]*$/, "").trim() || name;

const resolvePrincipleReceiptHeader = async (order) => {
    let principle = order?.principle;
    const principleId =
        typeof principle === "string" || typeof principle === "number"
            ? principle
            : principle?._id;

    const needsFetch =
        !principle ||
        typeof principle === "string" ||
        typeof principle === "number" ||
        !principle?.name ||
        PRINCIPLE_BUSINESS_DETAIL_ROWS.some(({ field }) => !(field in principle));

    if (needsFetch && principleId) {
        try {
            const res = await publicRequest.get(`/users/find/${principleId}`);
            principle = res.data;
        } catch (err) {
            console.error("Failed to load principle account for receipt:", err);
        }
    }

    if (!principle || typeof principle !== "object") {
        return DEFAULT_RECEIPT_HEADER;
    }

    const { addressLine1, addressLine2 } = formatPrincipleAddressLines(
        principle.address || {}
    );

    const rawName =
        String(principle.name || "").trim() || DEFAULT_RECEIPT_HEADER.name;
    const hasBusinessRegNo = String(principle.businessRegNo || "").trim() !== "";

    return {
        name: hasBusinessRegNo ? stripRegistrationFromName(rawName) : rawName,
        addressLine1,
        addressLine2,
        detailLines: PRINCIPLE_BUSINESS_DETAIL_ROWS.map(({ field, label }) => {
            const value = String(principle[field] || "").trim();
            return value ? `${label} : ${value}` : null;
        }).filter(Boolean),
    };
};

const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
        'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    if (num === 0) return 'Zero';

    const convertLessThanThousand = (n) => {
        if (n === 0) return '';
        if (n < 20) return ones[n];
        if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
        return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '');
    };

    const intPart = Math.floor(num);
    const decPart = Math.round((num - intPart) * 100);

    let result = '';
    if (intPart >= 1000000) {
        result += convertLessThanThousand(Math.floor(intPart / 1000000)) + ' Million ';
        result += convertLessThanThousand(Math.floor((intPart % 1000000) / 1000)) + (Math.floor((intPart % 1000000) / 1000) > 0 ? ' Thousand ' : '');
        result += convertLessThanThousand(intPart % 1000);
    } else if (intPart >= 1000) {
        result += convertLessThanThousand(Math.floor(intPart / 1000)) + ' Thousand ';
        result += convertLessThanThousand(intPart % 1000);
    } else {
        result = convertLessThanThousand(intPart);
    }

    result = result.trim() + ' Ringgit';
    if (decPart > 0) {
        result += ' and ' + convertLessThanThousand(decPart) + ' Sen';
    }
    return result + ' Only';
};

const wrapText = (text, font, size, maxWidth) => {
    const value = sanitizeText(text);
    if (!value) return ["-"];
    const words = value.split(/\s+/).filter(Boolean);
    const lines = [];
    let line = "";
    words.forEach((word) => {
        const next = line ? `${line} ${word}` : word;
        if (font.widthOfTextAtSize(next, size) <= maxWidth) {
            line = next;
        } else {
            if (line) lines.push(line);
            line = word;
        }
    });
    if (line) lines.push(line);
    return lines.length ? lines : ["-"];
};

const createOfficialReceipt = async (order, paymentType, paymentAmount, receiptNumber) => {
    try {
        const paymentSlot = PAYMENT_SLOT_BY_RECEIPT_TYPE[paymentType];

        const NumberFormatter = new Intl.NumberFormat("en-GB", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

        const pdfDoc = await PDFDocument.create();
        const timesRomanFont = await pdfDoc.embedFont(StandardFonts.TimesRoman);
        const timesRomanBoldFont = await pdfDoc.embedFont(StandardFonts.TimesRomanBold);
        const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
        const helveticaBoldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

        const page = pdfDoc.addPage([595, 842]);
        const { width, height } = page.getSize();

        const marginLeft = 50;
        const marginRight = 50;
        const contentRight = width - marginRight;
        const contentWidth = contentRight - marginLeft;
        const ink = rgb(0.1, 0.1, 0.1);
        const muted = rgb(0.34, 0.34, 0.34);
        const accent = rgb(0.1, 0.18, 0.36);
        const rule = rgb(0.52, 0.55, 0.62);
        const hairline = rgb(0.8, 0.8, 0.82);
        const stripe = rgb(0.965, 0.968, 0.976);

        const fitTextSize = (text, font, size, minSize, maxWidth) => {
            let fittedSize = size;
            while (
                fittedSize > minSize &&
                font.widthOfTextAtSize(text, fittedSize) > maxWidth
            ) {
                fittedSize -= 0.25;
            }
            return fittedSize;
        };

        const drawCentered = (text, y, size, font, color) => {
            page.drawText(text, {
                x: (width - font.widthOfTextAtSize(text, size)) / 2,
                y,
                size,
                font,
                color,
            });
        };

        let currentY = height - 48;
        let logoBottom = currentY;

        try {
            const logoBytes = await fetch(
                "https://ik.imagekit.io/dnddecpho/IHGroup/inhomelogo-new_80yctapLk.png"
            ).then((res) => res.arrayBuffer());
            const logoImage = await pdfDoc.embedPng(logoBytes);
            const jpgDims = logoImage.scale(88 / logoImage.width);
            page.drawImage(logoImage, {
                x: marginLeft,
                y: currentY - jpgDims.height + 14,
                width: jpgDims.width,
                height: jpgDims.height,
            });
            logoBottom = currentY - jpgDims.height + 14;
        } catch (e) {
            console.log("Logo not found");
        }

        const principleHeader = await resolvePrincipleReceiptHeader(order);
        const headerX = 148;
        const paidLabel = "PAID";
        const paidFontSize = 16;
        const paidTextWidth = helveticaBoldFont.widthOfTextAtSize(paidLabel, paidFontSize);
        const paidBoxHeight = 28;
        const paidBoxWidth = 128;
        const frameInnerRight = width - 31.5;
        const frameInnerTop = height - 27.5;
        const paidBoxX = frameInnerRight - paidBoxWidth;
        const paidBoxY = frameInnerTop - paidBoxHeight;
        const paidGreen = rgb(0.15, 0.7, 0.2);

        page.drawRectangle({
            x: paidBoxX,
            y: paidBoxY,
            width: paidBoxWidth,
            height: paidBoxHeight,
            color: paidGreen,
        });
        page.drawText(paidLabel, {
            x: paidBoxX + (paidBoxWidth - paidTextWidth) / 2,
            y: paidBoxY + 8,
            size: paidFontSize,
            font: helveticaBoldFont,
            color: rgb(1, 1, 1),
        });

        const headerMaxWidth = paidBoxX - headerX - 12;
        const headerNameText = sanitizeText(principleHeader.name);

        page.drawText(headerNameText, {
            x: headerX,
            y: currentY,
            size: fitTextSize(headerNameText, timesRomanBoldFont, 12, 8.5, headerMaxWidth),
            font: timesRomanBoldFont,
            color: ink,
        });

        const headerLines = [
            principleHeader.addressLine1,
            principleHeader.addressLine2,
            ...principleHeader.detailLines,
        ]
            .map((line) => sanitizeText(line))
            .filter(Boolean);

        headerLines.forEach((lineText, index) => {
            page.drawText(lineText, {
                x: headerX,
                y: currentY - 14 - index * 11,
                size: fitTextSize(lineText, timesRomanFont, 8.5, 7, headerMaxWidth),
                font: timesRomanFont,
                color: muted,
            });
        });

        const headerTextBottom = currentY - 14 - Math.max(headerLines.length - 1, 0) * 11;
        currentY = Math.min(logoBottom, headerTextBottom) - 18;

        page.drawLine({
            start: { x: marginLeft, y: currentY },
            end: { x: contentRight, y: currentY },
            thickness: 1.4,
            color: accent,
        });
        page.drawLine({
            start: { x: marginLeft, y: currentY - 3 },
            end: { x: contentRight, y: currentY - 3 },
            thickness: 0.4,
            color: rule,
        });

        currentY -= 28;
        drawCentered("OFFICIAL RECEIPT", currentY, 16, helveticaBoldFont, accent);

        currentY -= 16;
        page.drawLine({
            start: { x: marginLeft, y: currentY },
            end: { x: contentRight, y: currentY },
            thickness: 0.5,
            color: hairline,
        });

        currentY -= 20;

        const storedReceiptNo = resolveDisplayReceiptNo(
            order,
            paymentSlot,
            receiptNumber
        );
        const labelSize = 8.5;
        const valueSize = 10;
        const labelColWidth = 168;
        const valueX = marginLeft + labelColWidth;
        const valueMaxWidth = contentRight - valueX - 10;
        const lineGap = 12;

        const preparedRows = [
            ["Receipt No", storedReceiptNo || "-"],
            ["Date", moment().format("DD / MM / YYYY")],
            ["Payment Particular", getPaymentParticular(order, paymentSlot)],
            ["Received From", order.userId?.name || order.user?.name || "N/A"],
            ["Confirmation Order No", order.coNumber || "N/A"],
            ["Invoice No", order.invoiceId || "N/A"],
            ["The Sum of Ringgit", numberToWords(paymentAmount)],
            ["Payment Mode", getPaymentMode(order, paymentSlot)],
        ].map(([label, value]) => {
            const lines = wrapText(value, timesRomanFont, valueSize, valueMaxWidth);
            return {
                label,
                lines,
                rowH: Math.max(24, lines.length * lineGap + 10),
            };
        });

        const tableTop = currentY;
        const tableHeight = preparedRows.reduce((sum, row) => sum + row.rowH, 0);
        let rowY = tableTop;

        preparedRows.forEach((row, index) => {
            if (index % 2 === 0) {
                page.drawRectangle({
                    x: marginLeft,
                    y: rowY - row.rowH,
                    width: contentWidth,
                    height: row.rowH,
                    color: stripe,
                });
            }

            const textY = rowY - 15;
            page.drawText(row.label, {
                x: marginLeft + 10,
                y: textY,
                size: labelSize,
                font: helveticaFont,
                color: muted,
            });
            row.lines.forEach((line, lineIndex) => {
                page.drawText(line, {
                    x: valueX,
                    y: textY - lineIndex * lineGap,
                    size: valueSize,
                    font: timesRomanFont,
                    color: ink,
                });
            });
            rowY -= row.rowH;
        });

        page.drawRectangle({
            x: marginLeft,
            y: tableTop - tableHeight,
            width: contentWidth,
            height: tableHeight,
            borderColor: hairline,
            borderWidth: 0.7,
        });

        currentY = rowY - 36;

        const amountBoxWidth = 214;
        const amountBoxHeight = 48;
        const amountBoxY = currentY - 16 - amountBoxHeight;
        const signX = marginLeft + 248;
        const signWidth = contentRight - signX;
        const companyFor = `For ${sanitizeText(principleHeader.name)}`;
        const signLineY = currentY - 78;

        page.drawText("AMOUNT RECEIVED", {
            x: marginLeft,
            y: currentY + 6,
            size: 8,
            font: helveticaBoldFont,
            color: muted,
        });

        page.drawRectangle({
            x: marginLeft,
            y: amountBoxY,
            width: amountBoxWidth,
            height: amountBoxHeight,
            borderColor: accent,
            borderWidth: 1.2,
        });
        page.drawRectangle({
            x: marginLeft,
            y: amountBoxY,
            width: 46,
            height: amountBoxHeight,
            color: accent,
        });
        page.drawText("RM", {
            x: marginLeft + 11,
            y: amountBoxY + 18,
            size: 12,
            font: helveticaBoldFont,
            color: rgb(1, 1, 1),
        });
        page.drawText(NumberFormatter.format(paymentAmount), {
            x: marginLeft + 60,
            y: amountBoxY + 16,
            size: 16,
            font: timesRomanBoldFont,
            color: ink,
        });

        page.drawText(companyFor, {
            x: signX,
            y: currentY + 6,
            size: fitTextSize(companyFor, timesRomanBoldFont, 9, 7, signWidth),
            font: timesRomanBoldFont,
            color: ink,
        });
        page.drawLine({
            start: { x: signX, y: signLineY },
            end: { x: contentRight, y: signLineY },
            thickness: 0.7,
            color: ink,
        });
        page.drawText("Authorised Signature", {
            x: signX,
            y: signLineY - 14,
            size: 8,
            font: helveticaFont,
            color: muted,
        });
        page.drawText("Name :", {
            x: signX,
            y: signLineY - 32,
            size: 8.5,
            font: timesRomanFont,
            color: muted,
        });
        page.drawText("Received By :", {
            x: signX,
            y: signLineY - 48,
            size: 8.5,
            font: timesRomanFont,
            color: muted,
        });

        const footerY = signLineY - 72;
        page.drawLine({
            start: { x: marginLeft, y: footerY + 14 },
            end: { x: contentRight, y: footerY + 14 },
            thickness: 0.5,
            color: hairline,
        });
        drawCentered(
            "This is a computer-generated official receipt.",
            footerY,
            8,
            helveticaFont,
            muted
        );

        const frameBottom = footerY - 24;
        const frameTop = height - 24;
        page.drawRectangle({
            x: 28,
            y: frameBottom,
            width: width - 56,
            height: frameTop - frameBottom,
            borderColor: accent,
            borderWidth: 1.15,
        });
        page.drawRectangle({
            x: 31.5,
            y: frameBottom + 3.5,
            width: width - 63,
            height: frameTop - frameBottom - 7,
            borderColor: rule,
            borderWidth: 0.4,
        });

        // Serialize the PDFDocument to bytes
        const pdfBytes = await pdfDoc.save();

        // Download the PDF
        download(
            pdfBytes,
            `Official_Receipt_${paymentType}_${order.coNumber || order._id}_${moment().format("DDMMYYYY")}.pdf`,
            "application/pdf"
        );
    } catch (err) {
        console.error("Error creating official receipt:", err);
        alert("Error creating official receipt. Please try again.");
    }
};

export default createOfficialReceipt;
